import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Users,
  Search,
  Phone,
  Mail,
  Building2,
  Loader2,
  Smartphone,
  MapPin,
  Globe,
  Clock,
} from "lucide-react";


export default function ContactDirectoryPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchDirectory();
  }, [token]);

  useEffect(() => {
    filterStaff();
  }, [staff, searchQuery, selectedDepartment]);

  const fetchDirectory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/directory`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const staffData = response.data.staff || [];
      setStaff(staffData);
      
      // Extract unique departments
      const depts = [...new Set(staffData.map((s) => s.department).filter(Boolean))];
      setDepartments(depts);
    } catch (error) {
      console.error("Error fetching directory:", error);
      toast.error("Failed to load contact directory");
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = [...staff];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.first_name?.toLowerCase().includes(query) ||
          s.last_name?.toLowerCase().includes(query) ||
          s.department?.toLowerCase().includes(query) ||
          s.role?.toLowerCase().includes(query)
      );
    }
    
    if (selectedDepartment !== "all") {
      filtered = filtered.filter((s) => s.department === selectedDepartment);
    }
    
    setFilteredStaff(filtered);
  };

  // Group staff by department
  const groupedByDepartment = filteredStaff.reduce((acc, s) => {
    const dept = s.department || "General";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(s);
    return acc;
  }, {});

  // Department order priority
  const deptOrder = ["Management", "Sales", "Operations", "Customer Service", "Accounting", "Logistics", "Documentation", "Customs"];
  const sortedDepartments = Object.keys(groupedByDepartment).sort((a, b) => {
    const aIdx = deptOrder.indexOf(a);
    const bIdx = deptOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-[#003366] rounded-2xl mb-4">
            <Users className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Europacific Contacts</h1>
          <p className="text-slate-600 mt-2">
            Find the right person at Europacific to help you
          </p>
        </div>

        {/* Office Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#003366] to-[#004488] rounded-xl p-6 mb-8 text-white"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Head Office</p>
                <p className="text-blue-100 text-sm">
                  Ferrarska 14<br />
                  6000 Koper, Slovenia
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Main Line</p>
                <p className="text-blue-100 text-sm">
                  +386 5 663 2200
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Office Hours</p>
                <p className="text-blue-100 text-sm">
                  Mon-Fri: 8:00 - 16:00 CET
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search & Filter */}
        <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 text-sm"
            >
              <option value="all">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Staff Directory */}
        {filteredStaff.length === 0 ? (
          <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Contacts Found</h3>
            <p className="text-slate-500">
              {searchQuery 
                ? "Try a different search term"
                : "Contact directory is being updated"}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDepartments.map((department, deptIdx) => (
              <motion.div
                key={department}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: deptIdx * 0.1 }}
                className="bg-white rounded-xl border shadow-sm overflow-hidden"
              >
                <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#003366]" />
                  <h2 className="font-semibold text-[#003366]">{department}</h2>
                </div>
                <div className="divide-y">
                  {groupedByDepartment[department].map((member, idx) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#003366] to-[#0055aa] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                          {member.first_name?.[0]}{member.last_name?.[0]}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">
                            {member.first_name} {member.last_name}
                          </h3>
                          <p className="text-sm text-slate-600">{member.role}</p>
                          
                          {/* Contact Details */}
                          <div className="mt-2 flex flex-wrap gap-4 text-sm">
                            {member.email && (
                              <a
                                href={`mailto:${member.email}`}
                                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                              >
                                <Mail className="h-4 w-4" />
                                <span>{member.email}</span>
                              </a>
                            )}
                            {member.office_phone && (
                              <a
                                href={`tel:${member.office_phone}`}
                                className="flex items-center gap-1.5 text-slate-600 hover:text-slate-800"
                              >
                                <Phone className="h-4 w-4" />
                                <span>{member.office_phone}</span>
                              </a>
                            )}
                            {member.mobile_phone && (
                              <a
                                href={`tel:${member.mobile_phone}`}
                                className="flex items-center gap-1.5 text-slate-600 hover:text-slate-800"
                              >
                                <Smartphone className="h-4 w-4" />
                                <span>{member.mobile_phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* General Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-slate-100 rounded-xl p-6 text-center"
        >
          <p className="text-slate-600 mb-3">
            Can't find who you're looking for?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:info@europacific.com"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border hover:bg-slate-50 transition-colors"
            >
              <Mail className="h-4 w-4 text-[#003366]" />
              <span className="text-sm font-medium">info@europacific.com</span>
            </a>
            <a
              href="tel:+38656632200"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border hover:bg-slate-50 transition-colors"
            >
              <Phone className="h-4 w-4 text-[#003366]" />
              <span className="text-sm font-medium">+386 5 663 2200</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
